import { Box, Stack, Text } from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { useRef, useState } from 'react';

const MAX_SIZE_MB = 10;

export interface DropzoneFieldProps {
  label: string;
  withAsterisk?: boolean;
  accept?: string;
  hint?: string;
  value: File | null;
  onChange: (file: File | null) => void;
  /** e.g. "PDF, PNG, JPG" */
  acceptLabel?: string;
}

export function DropzoneField({
  label,
  withAsterisk,
  accept = 'image/*,application/pdf',
  hint,
  value,
  onChange,
  acceptLabel = 'PDF, PNG, JPG',
}: DropzoneFieldProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleClick = (): void => {
    inputRef.current?.click();
  };

  const handleFile = (file: File | null): void => {
    if (file && file.size > MAX_SIZE_MB * 1024 * 1024) {
      return;
    }
    onChange(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] ?? null;
    handleFile(file);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    if (file) handleFile(file);
  };

  return (
    <div>
      {label && (
        <Text size="sm" fw={600} mb="xs" c="#374151">
          {label}
          {withAsterisk ? (
            <Text component="span" c="red" ml={4}>
              *
            </Text>
          ) : null}
        </Text>
      )}
      {hint && (
        <Text size="xs" c="dimmed" mb="xs">
          {hint}
        </Text>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />
      <Box
        component="button"
        type="button"
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          display: 'block',
          width: '100%',
          padding: 24,
          borderRadius: 'var(--mantine-radius-md)',
          border: `2px dashed ${isDragOver ? 'var(--mantine-color-gold-5)' : '#9CA3AF'}`,
          backgroundColor: isDragOver ? 'var(--mantine-color-gold-0)' : '#FFFFFF',
          cursor: 'pointer',
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
          textAlign: 'center',
        }}
      >
        <Stack gap="xs" align="center">
          <IconUpload size={40} style={{ color: '#9CA3AF' }} />
          <Text size="sm" fw={600} c="#1F2937">
            {value ? value.name : 'Drop files here or click to upload'}
          </Text>
          <Text size="xs" c="dimmed">
            {value ? undefined : `${acceptLabel} up to ${MAX_SIZE_MB}MB`}
          </Text>
        </Stack>
      </Box>
    </div>
  );
}
